/* 사용량 측정(문제당 원가): AI 호출 1번 = 이벤트 1장, 문제 체류 시간 = study 이벤트.
   Firestore usage_events에 쌓고 집계는 별도 스크립트/대시보드에서 한다.
   원가(돈)는 저장하지 않는다 — 토큰만 사실로 남기고 단가는 집계 시점에 곱한다(단가 개정 대응). */
import { _db, _uid } from "./platform.js";

/* 제공자마다 토큰 필드명이 달라 {inTok, outTok}으로 정규화한다.
   usage를 못 받으면 null — 0으로 위장하면 원가가 실제보다 싸게 집계된다. */
export function normalizeUsage(u){
  if(!u)return null;
  const inTok=u.input_tokens??u.promptTokenCount??u.prompt_tokens;
  const outTok=u.output_tokens??u.candidatesTokenCount??u.completion_tokens;
  if(typeof inTok!=="number"||typeof outTok!=="number")return null;
  const out={inTok,outTok};
  // 캐시 토큰(Claude 전용): 읽기는 정가의 1/10, 생성은 1.25배 — 원가 정밀도를 위해 있을 때만 기록
  if(u.cache_read_input_tokens>0)out.cacheR=u.cache_read_input_tokens;
  if(u.cache_creation_input_tokens>0)out.cacheW=u.cache_creation_input_tokens;
  return out;
}

/* 세션 = 홈에서 "공부 시작"을 눌러 문제 여러 개를 이어서 공부하는 한 판.
   학습 화면 진입 시 startUsageSession()으로 새 id 발급, 학습 밖 이벤트(자료 인제스트 등)는 세션 null. */
let _sid=null;
let _code=null;   // 현재 학생코드 — AI든 study든 모든 이벤트에 공통 부여 (ai.js setAcademyMode가 갱신)
export function setUsageCode(c){_code=(typeof c==="string"&&c)||null;}
const _qnames={};  // qid → 사람이 읽는 문제(개념) 이름 — Study가 문제 선택 시 등록
export function registerQName(qid,name){if(qid&&name)_qnames[qid]=String(name).slice(0,80);}
export function startUsageSession(){
  _sid=Date.now().toString(36)+"-"+Math.random().toString(36).slice(2,7);
  return _sid;
}
export function currentSessionId(){return _sid;}

/* 업로드 세션 인계: 자료를 올리는 순간 세션을 열어 인제스트(OCR·개념추출·요약) 비용을 담고,
   그 덱으로 바로 이어지는 학습이 같은 세션을 물려받게 한다 — "올려서 공부한 한 판"이 한 줄로 남는다.
   표식은 일회용이다. 어느 덱이든 학습 화면에 처음 들어가는 순간 소멸한다: 남의 덱 학습이 업로드
   세션에 섞이지 않고, 같은 덱을 나중에 다시 펴는 건 별개의 판으로 잡힌다. */
const HOLD_MS=30*60*1000;   // ponytail: 30분 넘게 방치했으면 '이어지는 학습'이 아니다
let _hold=null;
export function holdSessionFor(deckId,now=Date.now()){_hold=deckId?{deckId,at:now}:null;}
export function claimSession(deckId,now=Date.now()){
  const h=_hold;_hold=null;
  return !!(h&&deckId&&h.deckId===deckId&&now-h.at<HOLD_MS);
}

/* 이벤트 조립. Firestore가 undefined 값을 거부하므로 빈 자리는 전부 null. */
export function buildUsageEvent(ctx={},res={}){
  const u=normalizeUsage(res.usage);
  return {
    ts:ctx.ts??Date.now(),
    type:ctx.type??"ai",
    uid:ctx.uid??null,
    code:ctx.code??null,   // 학생코드(=API key 식별자) — key별 필터용
    sessionId:ctx.sessionId??null,
    feature:ctx.feature??"other",
    subject:ctx.subject??null,
    unitId:ctx.unitId??null,
    qid:ctx.qid??null,
    qname:ctx.qname??null,
    nTotal:ctx.nTotal??null,   // 세션 시작 이벤트 전용: 덱의 전체 문제(개념) 수
    sname:ctx.sname??null,     // 세션 시작 이벤트 전용: 세션 이름(덱 이름 · 모드)
    smode:ctx.smode??null,     // 세션 시작 이벤트 전용: 학습 방식(개념강의/과정/플래시/시험)
    provider:res.provider??null,
    model:res.model??null,
    inTok:u?u.inTok:null,
    outTok:u?u.outTok:null,
    cacheR:(u&&u.cacheR)??null,
    cacheW:(u&&u.cacheW)??null,
    durMs:res.durMs??null,
    ok:res.ok??null,
    err:res.err??null,   // 실패 시 에러 메시지 원문 — "전원 동시 실패" 패턴 진단용 (자르지 않는다)
  };
}

/* 기록: 비동기 fire-and-forget — 측정 실패가 학습 흐름을 절대 방해하지 않는다.
   로그인 전(uid 없음)·Firestore 불가 시엔 조용히 건너뜀 (파일럿은 전원 로그인 전제). */
export function logUsage(ctx,res){
  try{
    if(!_db||!_uid)return;
    const ev=buildUsageEvent({uid:_uid,sessionId:currentSessionId(),code:_code,
      qname:(ctx&&ctx.qid&&_qnames[ctx.qid])||null,...ctx},res);
    _db.collection("usage_events").add(ev).catch(e=>console.warn("[usage] 기록 실패:",e&&e.message));
  }catch(e){console.warn("[usage]",e&&e.message);}
}

/* 세션 시작 표식: 이 판의 덱에 문제가 총 몇 개였는지 — "안 푼 문제 수" 계산용. */
export function logSessionStart(nTotal,sname,smode){
  logUsage({type:"session",nTotal:(typeof nTotal==="number"&&nTotal>0)?nTotal:null,
    sname:(typeof sname==="string"&&sname.trim())?sname.trim().slice(0,120):null,   // 업로드 세션은 개념 3개까지 싣는다
    smode:(typeof smode==="string"&&smode)?smode:null},{ok:true});
}

/* 문제 체류 시간(AI 호출 없는 고민 시간 포함) — 문제를 떠날 때 한 장. */
export function logStudyTime(qid,durMs,extra={}){
  logUsage({type:"study",qid,...extra},{ok:true,durMs});
}
